import { useState, useEffect } from 'react';
import { Plus, Trash2, Eye, FileText, RefreshCw, Package, ShoppingCart } from 'lucide-react';

const QuotationBuilder = ({ 
  onAddCalculation, 
  onViewQuotation, 
  onClear,
  onConvertToSale,
  currentCalculation = null 
}) => {
  const [quotation, setQuotation] = useState({
    customer_id: null,
    customer_name: '',
    calculations: [],
    created_at: new Date().toISOString()
  });

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('current_quotation');
    if (saved) {
      try {
        setQuotation(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse saved quotation:', e);
      }
    }
  }, []);

  // Persist to localStorage on change
  useEffect(() => {
    localStorage.setItem('current_quotation', JSON.stringify(quotation));
  }, [quotation]);

  // Add calculation to quotation
  const handleAddCalculation = () => {
    if (!currentCalculation) return;
    
    setQuotation(prev => ({
      ...prev,
      calculations: [
        ...prev.calculations,
        {
          id: Date.now(),
          ...currentCalculation,
          added_at: new Date().toISOString()
        }
      ]
    }));
    
    if (onAddCalculation) {
      onAddCalculation(currentCalculation);
    }
  };

  // Remove calculation from quotation
  const handleRemoveCalculation = (calcId) => {
    setQuotation(prev => ({
      ...prev,
      calculations: prev.calculations.filter(c => c.id !== calcId)
    }));
  };

  // Clear all calculations
  const handleClear = () => {
    setQuotation({
      customer_id: null,
      customer_name: '',
      calculations: [],
      created_at: new Date().toISOString()
    });
    if (onClear) onClear();
  };

  // Calculate totals
  const totals = quotation.calculations.reduce((acc, calc) => ({
    totalArea: acc.totalArea + (parseFloat(calc.total_area_sqm) || 0),
    totalCartons: acc.totalCartons + (parseInt(calc.total_cartons_needed) || parseInt(calc.cartons_needed) || 0),
    totalTiles: acc.totalTiles + (parseInt(calc.total_tiles_needed) || parseInt(calc.tiles_needed) || 0),
    totalCost: acc.totalCost + (parseFloat(calc.estimated_cost) || 0)
  }), { totalArea: 0, totalCartons: 0, totalTiles: 0, totalCost: 0 });

  const hasCalculations = quotation.calculations.length > 0;

  return (
    <div className="bg-white rounded-lg border border-secondary-lightGray shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-secondary-lightGray">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-accent-gold" />
          <h3 className="font-semibold text-primary-black">Quotation Builder</h3>
          {hasCalculations && (
            <span className="px-2 py-0.5 bg-accent-gold text-white text-xs font-bold rounded-full">
              {quotation.calculations.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasCalculations && (
            <button
              onClick={handleClear}
              className="flex items-center gap-1 px-2 py-1 text-red-500 hover:bg-red-50 rounded text-sm transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Add Button (when there's a current calculation) */}
      {currentCalculation && (
        <div className="p-3 border-b border-secondary-lightGray bg-accent-gold/5">
          <button
            onClick={handleAddCalculation}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent-gold text-white rounded-lg font-semibold hover:bg-accent-goldDark transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Current Calculation to Quotation
          </button>
        </div>
      )}

      {/* Calculations List */}
      {hasCalculations ? (
        <div className="max-h-60 overflow-y-auto">
          {quotation.calculations.map((calc, index) => (
            <div 
              key={calc.id} 
              className="flex items-center justify-between p-3 border-b border-secondary-lightGray last:border-0 hover:bg-secondary-offWhite"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-primary-slate text-white text-xs font-mono rounded">
                    #{index + 1}
                  </span>
                  <span className="text-sm font-medium text-primary-black truncate">
                    {calc.title || calc.calculation_number || 'Calculation'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-primary-slate">
                  <span>{calc.total_area_sqm || calc.total_area_sqm} m²</span>
                  <span>{calc.total_cartons_needed || calc.cartons_needed} cartons</span>
                  {calc.results && calc.results.length > 0 && (
                    <span>{calc.results.length} size(s)</span>
                  )}
                </div>
                {/* Product codes */}
                {calc.results && calc.results.some(r => r.product_code) && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {calc.results.filter(r => r.product_code).map((r, i) => (
                      <span key={i} className="text-xs font-mono text-accent-gold font-bold">
                        {r.product_code}
                      </span>
                    ))}
                  </div>
                )}
                {/* Estimated cost */}
                {calc.estimated_cost && (
                  <div className="mt-1">
                    <span className="text-xs text-primary-slate">Est: </span>
                    <span className="text-xs font-bold text-accent-gold">
                      ₦{parseFloat(calc.estimated_cost).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => handleRemoveCalculation(calc.id)}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-6 text-center text-primary-slate">
          <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No calculations added yet</p>
          <p className="text-xs mt-1">Complete a calculation and add it to build a quotation</p>
        </div>
      )}

      {/* Totals & Actions */}
      {hasCalculations && (
        <div className="p-4 border-t border-secondary-lightGray bg-secondary-offWhite">
          {/* Totals Summary */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center">
              <p className="text-lg font-bold text-primary-black">{totals.totalArea.toFixed(1)}</p>
              <p className="text-xs text-primary-slate">Total m²</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-accent-gold">{totals.totalCartons}</p>
              <p className="text-xs text-primary-slate">Total Cartons</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-primary-black">{totals.totalTiles}</p>
              <p className="text-xs text-primary-slate">Total Tiles</p>
            </div>
          </div>

      {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => onViewQuotation(quotation)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-secondary-lightGray rounded-lg text-primary-slate hover:bg-gray-50 transition-colors"
            >
              <Eye className="w-4 h-4" />
              View
            </button>
            {onConvertToSale && (
              <button
                onClick={() => onConvertToSale(quotation)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-accent-gold text-white rounded-lg font-semibold hover:bg-accent-goldDark transition-colors"
              >
                <ShoppingCart className="w-4 h-4" />
                Convert to Sale
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationBuilder;
